import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, formatDate } from "../lib/utils";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Tag,
  Loader2,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  FolderTree,
} from "lucide-react";

export default function CategoriesPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    subcategories: [],
  });
  const [newSubName, setNewSubName] = useState("");
  const [newSubCode, setNewSubCode] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", code: "", description: "", subcategories: [] });
    setEditing(null);
    setNewSubName("");
    setNewSubCode("");
  };

  const handleAddSubcategory = () => {
    if (!newSubName.trim() || !newSubCode.trim()) {
      toast.error("Subcategory name and code are required");
      return;
    }
    const subId = `sub_${Date.now().toString(36)}`;
    setFormData((prev) => ({
      ...prev,
      subcategories: [
        ...(prev.subcategories || []),
        { subcategory_id: subId, name: newSubName.trim(), code: newSubCode.trim().toUpperCase() },
      ],
    }));
    setNewSubName("");
    setNewSubCode("");
  };

  const handleRemoveSubcategory = (subId) => {
    setFormData((prev) => ({
      ...prev,
      subcategories: (prev.subcategories || []).filter((s) => s.subcategory_id !== subId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error("Name and code are required");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API}/categories/${editing}`, formData, { withCredentials: true });
        toast.success("Category updated");
        fetchCategories();
      } else {
        const response = await axios.post(`${API}/categories`, formData, { withCredentials: true });
        setCategories((prev) => [response.data, ...prev]);
        toast.success("Category created");
      }
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (cat) => {
    setEditing(cat.category_id);
    setFormData({
      name: cat.name,
      code: cat.code,
      description: cat.description || "",
      subcategories: cat.subcategories || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId, categoryName) => {
    setDeleting(categoryId);
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { withCredentials: true });
      toast.success(`Category "${categoryName}" deleted`);
      setCategories((prev) => prev.filter((c) => c.category_id !== categoryId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete category");
    } finally {
      setDeleting(null);
    }
  };

  const handleRequestDeletion = async (categoryId, categoryName) => {
    setDeleting(categoryId);
    try {
      await axios.post(`${API}/deletion-requests`, {
        entity_type: "category",
        entity_id: categoryId,
        entity_name: categoryName,
      });
      toast.success("Deletion request submitted for approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit deletion request");
    } finally {
      setDeleting(null);
    }
  };

  const toggleExpand = (catId) => {
    setExpandedCats((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const filteredCategories = categories.filter(
    (cat) =>
      cat.name.toLowerCase().includes(search.toLowerCase()) ||
      cat.code.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Categories</h1>
          <p className="text-muted-foreground mt-1">Manage product categories and subcategories</p>
        </div>
        {isSuperAdmin && (
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-category-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Category
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Category" : "Create Category"}</DialogTitle>
                <DialogDescription>
                  {editing ? "Update category and its subcategories." : "Add a new product category with optional subcategories."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      placeholder="e.g., Paper Bags"
                      value={formData.name}
                      onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      className="rounded-sm"
                      data-testid="category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Code *</Label>
                    <Input
                      placeholder="e.g., PB"
                      value={formData.code}
                      onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                      className="rounded-sm"
                      data-testid="category-code-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    placeholder="Optional description"
                    value={formData.description}
                    onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                    className="rounded-sm"
                  />
                </div>

                {/* Subcategories */}
                <div className="space-y-3 border-t pt-3">
                  <Label className="flex items-center gap-2">
                    <FolderTree className="w-4 h-4" /> Subcategories
                  </Label>
                  {(formData.subcategories || []).length > 0 && (
                    <div className="space-y-1">
                      {formData.subcategories.map((sub) => (
                        <div
                          key={sub.subcategory_id}
                          className="flex items-center justify-between bg-muted/50 rounded-sm px-3 py-1.5"
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <Badge variant="outline" className="font-mono text-xs">{sub.code}</Badge>
                            <span>{sub.name}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveSubcategory(sub.subcategory_id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Subcategory name"
                      value={newSubName}
                      onChange={(e) => setNewSubName(e.target.value)}
                      className="rounded-sm flex-1"
                      data-testid="subcategory-name-input"
                    />
                    <Input
                      placeholder="Code"
                      value={newSubCode}
                      onChange={(e) => setNewSubCode(e.target.value.toUpperCase())}
                      className="rounded-sm w-24"
                      data-testid="subcategory-code-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddSubcategory}
                      data-testid="add-subcategory-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-category-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editing ? "Update" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="category-search"
          />
        </div>
      </div>

      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Code</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Name</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Subcategories</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Created</TableHead>
                {isAdmin && (
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCategories.map((cat) => (
                <React.Fragment key={cat.category_id}>
                  <TableRow data-testid={`category-row-${cat.category_id}`}>
                    <TableCell>
                      {(cat.subcategories || []).length > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => toggleExpand(cat.category_id)}
                        >
                          {expandedCats[cat.category_id] ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono font-medium">{cat.code}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-muted-foreground" />
                        {cat.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {(cat.subcategories || []).length} subcategories
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(cat.created_at)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(cat)}
                            data-testid={`edit-category-${cat.category_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isSuperAdmin ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  data-testid={`delete-category-${cat.category_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Category</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Delete "{cat.name}" and all its subcategories? This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => handleDelete(cat.category_id, cat.name)}
                                    disabled={deleting === cat.category_id}
                                  >
                                    {deleting === cat.category_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-amber-600 hover:text-amber-700"
                              onClick={() => handleRequestDeletion(cat.category_id, cat.name)}
                              disabled={deleting === cat.category_id}
                              data-testid={`request-delete-category-${cat.category_id}`}
                            >
                              {deleting === cat.category_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {/* Expanded subcategories */}
                  {expandedCats[cat.category_id] &&
                    (cat.subcategories || []).map((sub) => (
                      <TableRow key={sub.subcategory_id} className="bg-muted/20">
                        <TableCell></TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground pl-8">{sub.code}</TableCell>
                        <TableCell className="pl-8">
                          <div className="flex items-center gap-2 text-sm">
                            <FolderTree className="w-3 h-3 text-muted-foreground" />
                            {sub.name}
                          </div>
                        </TableCell>
                        <TableCell colSpan={isAdmin ? 3 : 2}></TableCell>
                      </TableRow>
                    ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          {filteredCategories.length === 0 && (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No categories found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
